import { Link } from "react-router-dom";
import { ArrowRight, Apple, Play, Check, Zap, Users, HeartPulse, Volleyball, Smartphone, Tent, Facebook, Instagram, Twitter, Youtube, LogIn } from "lucide-react";
import { PageRenderer } from "@/components/sections/PageRenderer";
import { HomeWelcome } from "@/components/home/HomeWelcome";
import { SectionTitle } from "@/components/common/SectionTitle";
import { SplitWord } from "@/components/common/SplitWord";
import { TestimonialCard } from "@/components/cards/TestimonialCard";
import { OfferCard } from "@/components/cards/OfferCard";
import { LocationBlock } from "@/components/cards/LocationBlock";
import { RedImageBand } from "@/components/common/RedImageBand";
import { usePublicContent } from "@/lib/useContent";
import { useSite } from "@/context/SiteContext";

const IMG = {
  perf: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=900&q=70&auto=format&fit=crop",
  team: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=900&q=70&auto=format&fit=crop",
  family: "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=900&q=70&auto=format&fit=crop",
  volley: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=900&q=70&auto=format&fit=crop",
  online: "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?w=900&q=70&auto=format&fit=crop",
  camps: "https://images.unsplash.com/photo-1577896851231-70ef18881754?w=900&q=70&auto=format&fit=crop",
};

const PATHS = [
  { icon: Zap, title: "Performance Training", text: "Speed, power & explosiveness for competitive athletes.", to: "/services", image: IMG.perf },
  { icon: Users, title: "Team Training", text: "High-energy group conditioning that builds your squad.", to: "/services", image: IMG.team },
  { icon: HeartPulse, title: "Adult & Child Fitness", text: "Programs for every age, every level, every goal.", to: "/services", image: IMG.family },
  { icon: Volleyball, title: "Mavs Volleyball", text: "Youth & club volleyball development and competition.", to: "/mavs-volleyball", image: IMG.volley },
  { icon: Smartphone, title: "Online Coaching", text: "Train anywhere with the Trilogy App & live coaching.", to: "/contact", image: IMG.online },
  { icon: Tent, title: "Camps / Clinics", text: "Seasonal camps and skill clinics for all athletes.", to: "/events", image: IMG.camps },
];

const QUICK_LINKS = [
  { label: "Mavs Volleyball", to: "/mavs-volleyball" },
  { label: "Training Services", to: "/services" },
  { label: "Packages", to: "/packages" },
  { label: "Booking", to: "/booking" },
];

export default function Home() {
  return (
    <>
      <PageRenderer pageKey="home" />
      <HomeWelcome />
    </>
  );
}

export const HomeBody = () => {
  const { settings } = useSite();
  const { data: testimonials } = usePublicContent("testimonials");
  const { data: offers } = usePublicContent("offers");
  const { data: locations } = usePublicContent("locations");
  const social = settings.social || {};
  const socialItems = [
    { key: "facebook", Icon: Facebook, url: social.facebook },
    { key: "instagram", Icon: Instagram, url: social.instagram },
    { key: "twitter", Icon: Twitter, url: social.twitter },
    { key: "youtube", Icon: Youtube, url: social.youtube },
  ].filter((s) => s.url);

  return (
    <div>
      {/* hero via PageRenderer */}

      {/* Quick service paths */}
      <section id="home-paths" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle align="center" eyebrow="What We Offer" title={<SplitWord left="FIND YOUR " right="PATH" />} subtitle="Choose your starting point. Every program is built around real outcomes and led by elite coaches." />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PATHS.map((p) => {
              const Icon = p.icon;
              return (
                <Link key={p.title} to={p.to} data-testid="service-path-card" className="group relative overflow-hidden rounded-xl border border-border bg-card transition-colors hover:border-[hsl(var(--brand-red))]">
                  <div className="relative h-40 overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-md bg-brand text-white"><Icon className="h-5 w-5" /></div>
                    <h3 className="absolute bottom-3 left-4 display-title text-2xl text-white">{p.title}</h3>
                  </div>
                  <div className="flex items-center justify-between p-5">
                    <p className="text-sm text-muted-foreground">{p.text}</p>
                    <ArrowRight className="ml-3 h-5 w-5 shrink-0 text-brand transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Current offer */}
      {offers.length > 0 && (
        <section className="bg-muted/30 py-16 sm:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
            <SectionTitle eyebrow="Limited Time" title={<SplitWord left="CURRENT " right="OFFER" />} className="mb-8" />
            <OfferCard item={offers[0]} />
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="bg-background py-16 sm:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
            <SectionTitle eyebrow="Social Proof" title="Featured Athletes" subtitle="Real results from real members of the Trilogy family." />
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {testimonials.slice(0, 4).map((t) => <TestimonialCard key={t.id} item={t} />)}
            </div>
            <div className="mt-8"><Link to="/testimonials" className="inline-flex items-center gap-2 font-condensed text-sm font-semibold uppercase tracking-wide text-brand hover:gap-3 transition-all">See all stories <ArrowRight className="h-4 w-4" /></Link></div>
          </div>
        </section>
      )}

      {/* Trilogy App */}
      <section className="relative overflow-hidden bg-black py-16 sm:py-24 text-white grain">
        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 text-center">
          <img src={settings.logo_url} alt={settings.brand_name} className="mx-auto mb-6 h-40 w-auto sm:h-48" data-testid="home-app-logo" />
          <h2 className="display-title text-5xl sm:text-6xl"><SplitWord left="TRILOGY" right="APP" /></h2>
          <p className="mx-auto mt-4 max-w-2xl text-white/70 leading-relaxed">Work with the best trainers around to achieve a life of fitness and a higher quality of health. We customize plans to fit your busy schedule.</p>
          <ul className="mx-auto mt-6 grid max-w-2xl gap-2 text-left sm:grid-cols-2">
            {(settings.app_features || []).map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-white/80"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{f}</li>
            ))}
          </ul>
          <p className="mt-7 display-title text-3xl"><SplitWord left="14 DAY " right="FREE" /> <SplitWord left="ONLINE " right="TRIAL" /></p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href={settings.app_store_url} target="_blank" rel="noopener noreferrer" data-testid="app-store-link" className="inline-flex items-center gap-2 rounded-md bg-white px-6 py-3 text-sm font-semibold text-black hover:bg-white/90 transition-colors"><Apple className="h-5 w-5" /> App Store</a>
            <a href={settings.google_play_url} target="_blank" rel="noopener noreferrer" data-testid="google-play-link" className="inline-flex items-center gap-2 rounded-md bg-white px-6 py-3 text-sm font-semibold text-black hover:bg-white/90 transition-colors"><Play className="h-5 w-5" /> Google Play</a>
            <Link to="/login" data-testid="home-app-portal-login" className="inline-flex items-center gap-2 rounded-md border border-white/30 px-6 py-3 text-sm font-semibold uppercase text-white hover:bg-white/10 transition-colors"><LogIn className="h-4 w-4" /> Login / Portal</Link>
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="bg-background py-12">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {QUICK_LINKS.map((q) => (
              <Link key={q.to} to={q.to} data-testid="home-quick-link" className="flex items-center justify-between rounded-xl border border-border bg-card px-5 py-4 transition-colors hover:border-[hsl(var(--brand-red))]">
                <span className="font-condensed text-lg font-semibold uppercase tracking-wide">{q.label}</span>
                <ArrowRight className="h-5 w-5 text-brand" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Locations preview */}
      {locations.length > 0 && (
        <section id="home-locations" className="scroll-mt-24 bg-muted/30 py-16 sm:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
            <SectionTitle eyebrow="Find Us" title={<SplitWord left="TWO " right="LOCATIONS" />} subtitle="Train with us in Lenexa, KS and Independence, MO." />
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {locations.map((l) => <LocationBlock key={l.id} item={l} />)}
            </div>
          </div>
        </section>
      )}

      {/* Red-tone image band */}
      <RedImageBand
        eyebrow="No Excuses"
        title="TRAIN LIKE IT MATTERS"
        image="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1600&q=70&auto=format&fit=crop"
      >
        <p className="max-w-xl text-lg text-white/90">Every rep counts. Join a community built on intensity, accountability, and results.</p>
        <a href={settings.exercise_booking_url} target="_blank" rel="noopener noreferrer" data-testid="home-redband-cta" className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-white px-7 py-3.5 text-sm font-semibold uppercase tracking-wide text-[hsl(var(--brand-red-deep))] hover:bg-white/90 transition-colors active:scale-[0.98]">Start Training</a>
      </RedImageBand>

      {/* Social strip */}
      {socialItems.length > 0 && (
        <section className="bg-background py-12">
          <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-4 px-4 sm:px-6">
            <p className="font-condensed text-sm font-semibold uppercase tracking-[0.25em] text-muted-foreground">Follow The Movement</p>
            <div className="flex items-center gap-3">
              {socialItems.map(({ key, Icon, url }) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={key} className="flex h-11 w-11 items-center justify-center rounded-full border border-border text-foreground hover:border-brand hover:text-brand transition-colors"><Icon className="h-5 w-5" /></a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section id="home-cta" className="scroll-mt-24 relative overflow-hidden bg-brand py-16 sm:py-20 text-white">
        <div className="relative mx-auto flex w-full max-w-5xl flex-col items-center gap-5 px-4 sm:px-6 text-center">
          <h2 className="display-title text-4xl sm:text-6xl">Start With An Intro Workout</h2>
          <p className="max-w-xl text-white/90">Your first session is free. Meet a coach, tour the facility, and build your plan.</p>
          <a href={settings.exercise_booking_url} target="_blank" rel="noopener noreferrer" data-testid="home-final-cta-button" className="inline-flex items-center rounded-md bg-black px-8 py-4 text-base font-semibold uppercase tracking-wide text-white hover:bg-black/80 transition-colors active:scale-[0.98]">Book Intro Workout</a>
        </div>
      </section>
    </div>
  );
}
